<template>
    <div>
        <div v-if="isMobile">
            <v-switch
                :input-value="musicVolume > 0"
                @change="toggleMusicEnabled"
                hide-details
                dense
                color="secondary"
                :label="$t('user.soundSettings.musicLabel')"
            />
            <v-switch
                :input-value="buttonVolume > 0"
                @change="toggleButtonEnabled"
                hide-details
                dense
                color="secondary"
                class="mt-2"
                :label="$t('user.soundSettings.soundsLabel')"
            />
        </div>
        <div v-else>
            <v-slider
                v-model="musicVolumeModel"
                @change="saveVolumeSettings"
                prepend-icon="mdi-music-note"
                color="secondary"
                track-color="grey lighten-2"
                thumb-color="secondary"
                max="100"
                min="0"
                hide-details
                :label="$t('user.soundSettings.musicLabel')"
            />
            <v-slider
                v-model="buttonVolumeModel"
                @change="saveVolumeSettings"
                prepend-icon="mdi-gesture-tap"
                color="secondary"
                track-color="grey lighten-2"
                thumb-color="secondary"
                max="100"
                min="0"
                hide-details
                :label="$t('user.soundSettings.soundsLabel')"
                class="mt-4"
            />
        </div>
        <v-switch
            v-model="notificationsEnabledDraft"
            @change="saveVolumeSettings"
            hide-details
            dense
            color="secondary"
            class="mt-4"
            :label="$t('user.soundSettings.notificationsLabel')"
        />
        <v-row align="center" no-gutters class="mt-2">
            <v-col>
                <v-select
                    v-model="notificationSoundDraft"
                    @change="saveVolumeSettings"
                    :items="notificationSoundOptions"
                    item-text="label"
                    item-value="key"
                    :disabled="!notificationsEnabledDraft"
                    prepend-icon="mdi-bell-ring-outline"
                    color="secondary"
                    hide-details
                    dense
                    :label="$t('user.soundSettings.notificationSoundLabel')"
                />
            </v-col>
            <v-col cols="auto">
                <v-btn icon @click="previewNotificationSound">
                    <v-icon>mdi-play</v-icon>
                </v-btn>
            </v-col>
        </v-row>
    </div>
</template>

<script>
    import {mapActions, mapGetters} from 'vuex';
    import {API_ENTRYPOINT} from '@/config/entrypoint';
    import {isMobileMixin} from '@/utils/isMobileMixin';
    import {NOTIFICATION_SOUNDS, DEFAULT_NOTIFICATION_SOUND, getNotificationSoundFile} from '@/config/notificationSounds';
    import {setStoredMusicVolume, setStoredButtonVolume} from '@/utils/volumeStorage';
    import {playNotificationSound} from '@/utils/notificationAudio';

    export default {
        name: 'SoundSettings',
        mixins: [isMobileMixin],
        data() {
            return {
                // posledni nenulova hodnota pred vypnutim (mobilni prepinac) - zapnuti tak obnovi puvodni hlasitost misto skoku na pevnou hodnotu
                lastMusicVolume: 100,
                lastButtonVolume: 100,
                // neinicializovat z this.user primo tady - v okamziku data() jeste computed (mapGetters nize) nejsou k dispozici, skutecna hodnota se doplni v created()
                notificationSoundDraft: DEFAULT_NOTIFICATION_SOUND,
                notificationSoundOptions: NOTIFICATION_SOUNDS,
                notificationsEnabledDraft: true,
            }
        },
        created() {
            // kontrola proti katalogu, ne jen "je tam neco ulozene" - stary ucet muze mit ulozeny uz smazany klic, na ktery by v-select nenasel polozku
            if (this.user && NOTIFICATION_SOUNDS.some((s) => s.key === this.user.notificationSound)) {
                this.notificationSoundDraft = this.user.notificationSound;
            }
            if (this.user && typeof this.user.notificationsEnabled === 'boolean') {
                this.notificationsEnabledDraft = this.user.notificationsEnabled;
            }
        },
        computed: {
            ...mapGetters({
                musicVolume: 'journey/musicVolume',
                buttonVolume: 'journey/buttonVolume',
                user: 'user/item',
            }),
            musicVolumeModel: {
                get() {
                    return this.musicVolume;
                },
                set(v) {
                    this.musicVolumeSetup(v);
                },
            },
            buttonVolumeModel: {
                get() {
                    return this.buttonVolume;
                },
                set(v) {
                    this.buttonsVolumeSetup(v);
                },
            },
        },
        methods: {
            ...mapActions({
                musicVolumeSetup: 'journey/musicVolumeSetup',
                buttonsVolumeSetup: 'journey/buttonsVolumeSetup',
                fetchTrainer: 'user/fetchObject',
                editUser: 'user/editUser',
            }),
            toggleMusicEnabled(v) {
                if (v) {
                    this.musicVolumeSetup(this.lastMusicVolume > 0 ? this.lastMusicVolume : 100);
                } else {
                    if (this.musicVolume > 0) {
                        this.lastMusicVolume = this.musicVolume;
                    }
                    this.musicVolumeSetup(0);
                }
                this.saveVolumeSettings();
            },
            toggleButtonEnabled(v) {
                if (v) {
                    this.buttonsVolumeSetup(this.lastButtonVolume > 0 ? this.lastButtonVolume : 100);
                } else {
                    if (this.buttonVolume > 0) {
                        this.lastButtonVolume = this.buttonVolume;
                    }
                    this.buttonsVolumeSetup(0);
                }
                this.saveVolumeSettings();
            },
            // lokalni (per-zarizeni) ulozeni navic k serveru - serverova hodnota uz slouzi jen jako seed pro nove zarizeni, viz MusicPlayer.vue/volumeStorage.js
            saveVolumeSettings() {
                setStoredMusicVolume(this.user.id, this.musicVolume);
                setStoredButtonVolume(this.user.id, this.buttonVolume);
                const url = new URL('api/trainers/' + this.user.id, API_ENTRYPOINT);
                const obj = {
                    url,
                    obj: {
                        musicVolume: this.musicVolume,
                        buttonVolume: this.buttonVolume,
                        notificationSound: this.notificationSoundDraft,
                        notificationsEnabled: this.notificationsEnabledDraft,
                    },
                    method: 'PATCH',
                };
                this.fetchTrainer(obj).then((data) => {
                    this.editUser(data);
                });
            },
            previewNotificationSound() {
                // Notifikace nejedou pres "Sounds" (buttonVolume) - vzdy na plnou, viz App.vue/TeamBottomSheet.vue/PvpBattleScreen.vue.
                const file = getNotificationSoundFile(this.notificationSoundDraft);
                playNotificationSound(file, 1);
            },
        },
    }
</script>
